<script setup lang="ts">
import { useCurrentUser } from 'vuefire'
import LoggedInDialog from './onboarding/components/LoggedDuringOnboardingInDialog.vue'
import { EmployerOnboardingStatus } from '@swipe4work/api-client-fetch'

definePage({
  name: '/onboarding',
  path: '/onboarding',
  meta: {
    layout: 'onboarding',
    allowAnonymous: true,
  },
  redirect: '/onboarding/register',
})

const onboardingStore = useOnboardingStore()
const authStore = useAuthStore()
const toast = useToast()
const user = useCurrentUser()
const route = useRoute()
const dialog = useDynamicDialog()
const router = useRouter()
const t = useI18n()

const { items, isLoading, hasInviteCode, onboardingStatus, invite } = storeToRefs(onboardingStore)
const { isJwtAuth, isOnboardingUser } = storeToRefs(authStore)

/*
 * TODO: werkt dit ook voor MSAL? Wat moeten we hier mee?
 * TODO: kan ik hier isAuthenticated gebruiken?
 */

const showNormalFlow = computed(() =>
  isJwtAuth.value
|| isOnboardingUser.value || (onboardingStore.onboardingStatus?.status === EmployerOnboardingStatus.PartiallyCompleted || onboardingStore.onboardingStatus?.status === EmployerOnboardingStatus.Ready))

// Check for invite in query parameters and validate invite status
const inviteParam = route.query.invite as string

if (inviteParam) {
  const onboardingStatus = await onboardingStore.checkInviteStatus(inviteParam)
  if (onboardingStatus?.status === EmployerOnboardingStatus.PartiallyCompleted) {
    onboardingStore.goToPage(1)
  }
}

onBeforeMount(() => {
  if (user.value) {
    // If the user is already logged in, check if they are onboarded
    const loggedInAsInvite = user.value.email === invite.value?.email

    const isOnboarded = onboardingStatus?.value?.status === EmployerOnboardingStatus.Completed
    if (isOnboarded && (loggedInAsInvite || !hasInviteCode.value)) {
      // If the user is already onboarded, redirect to home
      router.replace('/')
      toast.info(t.t('onboarding.onboardingCompleteToast.detail'),{
        summary: t.t('onboarding.onboardingCompleteToast.summary'),
      })

      return
    }

    // If the user is logged in and has an invite code, show the dialog
    if (hasInviteCode.value) {
      dialog.open(LoggedInDialog, {
        props: {
          showHeader: false,
          closable: false,
          closeOnEscape: false,
          dismissableMask: false,
          draggable: false,
          style: {
            height: 'auto',
            width: 'auto',
            minWidth: '30rem',
          },
        },
        data: {
          loggedInAsInvite,
          email: user.value.email,
          employerInvite: onboardingStore.invite,
        },
        emits: {
          onContinue: () => {
            onboardingStore.goToPage(1)
          },
          onLogout: () => {
            authStore.logout()
            window.location.reload()
          },
        },
      })
    }

  }
})

const { activeIndex } = storeToRefs(onboardingStore)
</script>

<template>
  <div v-if="isLoading" class="min-h-64 flex items-center justify-center">
    <ProgressSpinner />
    <span class="ml-3">{{ $t('onboarding.invite.checking') }}</span>
  </div>
  <InviteInvalid v-else-if="!showNormalFlow" :has-invite-code="hasInviteCode" :onboarding-status="onboardingStatus" class="mx-auto max-w-3xl" />
  <template v-else>
    <div class="card mb-2" v-if="items.length > 1">
      <Steps :model="items" :active-step="activeIndex" :readonly="true" />
    </div>
    <router-view v-slot="{ Component }">
      <keep-alive>
        <component :is="Component" />
      </keep-alive>
    </router-view>
  </template>
</template>
